/**
 * Pagina /tesoreria/entes-externos — Sprint 18 P14 (lectura).
 *
 * El tesorero ve los entes externos al barrio y los recursos donados a
 * cada uno (CR citofonia porton, CR camion basura, llavero cartero).
 * Edicion vive en `/sa/entes-externos` (D-P14-5).
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  crearRecursoExternoDirecto,
  listEntesExternos,
  listRecursosDeEnteExterno,
  type CrearRecursoExternoPayload,
  type EnteExternoRead,
  type RecursoDeEnteExternoRead,
} from '../../lib/api/entes-externos';
import { actualizarRecurso } from '../../lib/api/recursos';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { InputFecha } from '../../components/ui/InputFecha';
import { InputMontoCLP } from '../../components/ui/InputMontoCLP';
import { formatFecha } from '../../lib/format';
import { RECURSO_TIPO_LABEL, etiquetaTipoRecurso } from '../../lib/labels';
import { Monto, Num } from '../../components/tesoreria/Monto';

const TIPOS_RECURSO_EXTERNO: CrearRecursoExternoPayload['tipo'][] = [
  'control_remoto',
  'llavero',
  'otro',
];

function mensajeError(err: unknown): string {
  if (err instanceof ApiError) {
    // Preferir el `detail` accionable de FastAPI (ej. "El ente externo está
    // desactivado..."); fallback al código si no viene.
    if (
      typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
    ) {
      return String((err.body as { detail: unknown }).detail);
    }
    return `Error ${err.status}`;
  }
  return 'Error de red';
}

interface ModalRecursosProps {
  ente: EnteExternoRead;
  onCerrar: () => void;
}

function ModalRecursos({ ente, onCerrar }: ModalRecursosProps) {
  const [recursos, setRecursos] = useState<RecursoDeEnteExternoRead[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // Edición inline del serial RFID (solo llaveros).
  const [editId, setEditId] = useState<string | null>(null);
  const [serialDraft, setSerialDraft] = useState('');
  const [guardando, setGuardando] = useState(false);

  function recargar() {
    listRecursosDeEnteExterno(ente.id)
      .then(setRecursos)
      .catch((err) => setError(mensajeError(err)));
  }

  useEffect(() => {
    recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ente.id]);

  function iniciarEdicion(r: RecursoDeEnteExternoRead) {
    setEditId(r.id);
    setSerialDraft(r.serial_rfid ?? '');
    setError(null);
  }

  async function guardarSerial(r: RecursoDeEnteExternoRead) {
    const serial = serialDraft.trim();
    if (!serial || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await actualizarRecurso(r.id, { serial_rfid: serial });
      setEditId(null);
      setSerialDraft('');
      recargar();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium text-text-primary">
              Recursos de {ente.nombre}
            </h2>
            {ente.contacto && (
              <p className="text-xs text-text-muted mt-0.5">
                Contacto: {ente.contacto}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {recursos === null && !error && (
          <p className="text-sm text-text-muted">Cargando…</p>
        )}
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        {recursos !== null && recursos.length === 0 && (
          <p className="text-sm text-text-muted">
            Este ente no tiene recursos asignados.
          </p>
        )}
        {recursos !== null && recursos.length > 0 && (
          <ul className="divide-y divide-white/[0.08]">
            {recursos.map((r) => (
              <li key={r.id} className="py-2 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-text-primary font-medium">
                      {etiquetaTipoRecurso(r.tipo)}
                    </span>
                    <Num className="ml-2 text-text-secondary">
                      {r.identificador}
                    </Num>
                  </div>
                  <span className="text-text-muted text-xs">
                    {r.estado}
                  </span>
                </div>
                <div className="text-xs text-text-muted mt-0.5">
                  Entregado {formatFecha(r.entregado_at)} ·{' '}
                  <Monto value={r.costo_reposicion} />
                  {r.notas && (
                    <span className="block italic mt-1">{r.notas}</span>
                  )}
                </div>
                {r.tipo === 'llavero' &&
                  (editId === r.id ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <input
                        type="text"
                        value={serialDraft}
                        onChange={(e) => setSerialDraft(e.target.value)}
                        maxLength={50}
                        placeholder="Serial RFID"
                        autoFocus
                        className="flex-1 min-w-0 bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1 text-text-primary font-mono text-xs"
                      />
                      <Button
                        variant="primary"
                        type="button"
                        onClick={() => guardarSerial(r)}
                        disabled={!serialDraft.trim() || guardando}
                      >
                        {guardando ? 'Guardando…' : 'Guardar'}
                      </Button>
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => setEditId(null)}
                        disabled={guardando}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-0.5 flex items-baseline gap-2 text-xs">
                      <span className="text-text-muted">Serial RFID:</span>
                      <Num className="text-text-secondary">
                        {r.serial_rfid ?? '— sin serial —'}
                      </Num>
                      <button
                        type="button"
                        onClick={() => iniciarEdicion(r)}
                        className="text-orange hover:underline"
                      >
                        {r.serial_rfid ? 'Editar' : 'Agregar'}
                      </button>
                    </div>
                  ))}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ModalCargarRecursoProps {
  ente: EnteExternoRead;
  onCerrar: () => void;
  onCargado: () => void;
}

/**
 * S19 cabo #5 — el tesorero carga un recurso donado a un ente externo
 * (CR de la citofonía, llavero del cartero, etc.). Crea el Recurso directo
 * (sin consumir stock, sin IngresoVario — es donación operativa).
 */
function ModalCargarRecurso({
  ente,
  onCerrar,
  onCargado,
}: ModalCargarRecursoProps) {
  const [tipo, setTipo] =
    useState<CrearRecursoExternoPayload['tipo']>('control_remoto');
  const [identificador, setIdentificador] = useState('');
  const [serialRfid, setSerialRfid] = useState('');
  const [costo, setCosto] = useState<number | null>(null);
  const [entregadoAt, setEntregadoAt] = useState('');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmar() {
    if (submitting) return;
    if (!identificador.trim()) {
      setError('El identificador es obligatorio.');
      return;
    }
    if (!costo || costo <= 0) {
      setError('El costo de reposición debe ser mayor a 0.');
      return;
    }
    if (!entregadoAt) {
      setError('La fecha de entrega es obligatoria.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await crearRecursoExternoDirecto(ente.id, {
        tipo,
        identificador: identificador.trim(),
        costo_reposicion: costo,
        entregado_at: entregadoAt,
        notas: notas.trim() || null,
        // H9: el serial solo aplica a llaveros; para otros tipos se envía null.
        serial_rfid:
          tipo === 'llavero' && serialRfid.trim() ? serialRfid.trim() : null,
      });
      onCargado();
      onCerrar();
    } catch (err) {
      setError(mensajeError(err));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Cargar recurso donado"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <h2 className="text-lg font-medium text-text-primary">
          Cargar recurso a {ente.nombre}
        </h2>
        <p className="text-sm text-text-secondary">
          Registra un recurso entregado a este ente externo. No genera cobro
          (es donación operativa del barrio) ni consume stock.
        </p>

        <div>
          <label className="block text-sm text-text-secondary mb-1">
            Tipo
          </label>
          <select
            value={tipo}
            onChange={(e) =>
              setTipo(e.target.value as CrearRecursoExternoPayload['tipo'])
            }
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary"
          >
            {TIPOS_RECURSO_EXTERNO.map((t) => (
              <option key={t} value={t} className="bg-bg-sunken">
                {RECURSO_TIPO_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-text-secondary mb-1">
            Identificador <span className="text-state-danger">*</span>
          </label>
          <input
            type="text"
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            maxLength={50}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary font-mono"
            placeholder="Ej: CR-CITOFONIA, LL-CARTERO"
          />
        </div>

        {tipo === 'llavero' && (
          <div>
            <label className="block text-sm text-text-secondary mb-1">
              Serial RFID
            </label>
            <input
              type="text"
              value={serialRfid}
              onChange={(e) => setSerialRfid(e.target.value)}
              maxLength={50}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary font-mono"
              placeholder="Serial del llavero (ej: 0007654321)"
            />
            <p className="mt-1 text-xs text-text-muted">
              Opcional. El número grabado en el llavero que abre el acceso.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm text-text-secondary mb-1">
            Costo de reposición <span className="text-state-danger">*</span>
          </label>
          <InputMontoCLP
            value={costo}
            onChange={setCosto}
            aria-label="Costo de reposición"
          />
        </div>

        <div>
          <label className="block text-sm text-text-secondary mb-1">
            Fecha de entrega <span className="text-state-danger">*</span>
          </label>
          <InputFecha
            value={entregadoAt}
            onChange={setEntregadoAt}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary"
          />
        </div>

        <div>
          <label className="block text-sm text-text-secondary mb-1">
            Notas
          </label>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary"
            placeholder="Opcional"
          />
        </div>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmar} disabled={submitting}>
            {submitting ? 'Cargando…' : 'Cargar recurso'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function EntesExternosPage() {
  const [entes, setEntes] = useState<EnteExternoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [recursosDe, setRecursosDe] = useState<EnteExternoRead | null>(null);
  const [cargarEn, setCargarEn] = useState<EnteExternoRead | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listEntesExternos({ incluir_inactivos: incluirInactivos })
      .then((data) => {
        if (!cancelado) setEntes(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(mensajeError(err));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [incluirInactivos]);

  return (
    <div className="px-4 md:px-8 py-6 space-y-5">
      <div>
        <h1 className="text-xl font-medium text-text-primary">
          Entes externos
        </h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Entidades externas al barrio (citofonía, basura, correos) a las
          que se entregan recursos sin cobro. La edición de estos entes
          la maneja el super_admin.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={incluirInactivos}
          onChange={(e) => setIncluirInactivos(e.target.checked)}
        />
        <span>Incluir desactivados</span>
      </label>

      {okMsg && (
        <div
          className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary"
          role="status"
        >
          {okMsg}
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}
      {!loading && !error && entes.length === 0 && (
        <Card>
          <p className="text-sm text-text-muted">
            No hay entes externos registrados.
          </p>
        </Card>
      )}
      {!loading && !error && entes.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {entes.map((e) => (
            <Card key={e.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-base font-medium text-text-primary">
                    {e.nombre}
                  </div>
                  {e.contacto && (
                    <div className="text-xs text-text-muted mt-0.5">
                      {e.contacto}
                    </div>
                  )}
                  {e.notas && (
                    <div className="text-xs text-text-secondary mt-1 italic">
                      {e.notas}
                    </div>
                  )}
                </div>
                {!e.activo && (
                  <span className="text-[10px] uppercase tracking-wide text-text-muted">
                    desactivado
                  </span>
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                {e.activo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setOkMsg(null); // limpia el banner de una carga previa
                      setCargarEn(e);
                    }}
                  >
                    Cargar recurso
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRecursosDe(e)}
                >
                  Ver recursos
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {recursosDe && (
        <ModalRecursos
          ente={recursosDe}
          onCerrar={() => setRecursosDe(null)}
        />
      )}

      {cargarEn && (
        <ModalCargarRecurso
          ente={cargarEn}
          onCerrar={() => setCargarEn(null)}
          onCargado={() =>
            setOkMsg(`Recurso cargado a ${cargarEn.nombre}.`)
          }
        />
      )}
    </div>
  );
}
